import {
  useMemo,
  forwardRef,
  useCallback,
  useEffect,
  useState,
  ReactNode
} from 'react';

import { BsLightningChargeFill } from 'react-icons/bs';
import {
  RiAddLine,
  RiInfinityLine,
  RiShieldFill,
  RiSubtractLine
} from 'react-icons/ri';

import { getHealthFrequencyValue } from '@common/calculations';
import { ExplainerPopover } from '@elements/widgets';
import Recommendation from '@imgs/health/quote/recommendation.png';
import type { SumInsuredValue } from '@store/healthquote';
import {
  ActionIcon,
  Badge,
  Box,
  Flex,
  Group,
  Slider,
  SliderProps,
  Stack,
  Text,
  Input,
  useMediaQuery,
  useDebouncedCallback
} from '@ui';
import { convertToINR, isUnlimitedSI } from '@utils';

import classes from './SliderCoverPicker.module.css';

interface SliderCoverPickerProps extends Omit<SliderProps, 'value' | 'onChange'> {
  title?: string;
  description?: string;
  coverChangeBox?: boolean;
  allowedCovers?: SumInsuredValue[];
  recommended?: number;
  error?: string;
  useEquidistantScale?: boolean;
  premiums?: { cover: SumInsuredValue; premium: number; tenure?: number }[];
  loading?: boolean;
  value?: SumInsuredValue;
  onChange?: (value: SumInsuredValue) => void;
}

const formatCoverLabel = (cover: SumInsuredValue): ReactNode =>
  isUnlimitedSI(cover) ? (
    <RiInfinityLine
      size={18}
      aria-label="Unlimited"
      style={{ verticalAlign: 'middle' }}
    />
  ) : (
    (convertToINR(cover, 0) as string)
  );

const SLIDER_EDGE_INSET = 8;
const RECOMMENDATION_DOT_SIZE = 16;

// Left offset (against the full-width wrapper) that centres an element of
// `elementWidth` on the slider mark sitting at `position` percent of the track.
const getMarkAlignedLeft = (position: number, elementWidth: number) => {
  const offset =
    SLIDER_EDGE_INSET - elementWidth / 2 - (position / 100) * SLIDER_EDGE_INSET * 2;

  return `calc(${position}% ${offset < 0 ? '-' : '+'} ${Math.abs(offset).toFixed(
    2
  )}px)`;
};

const SliderCoverPicker = forwardRef<HTMLDivElement, SliderCoverPickerProps>(
  (
    {
      title,
      description,
      coverChangeBox = true,
      allowedCovers,
      marks,
      value,
      restrictToMarks = true,
      recommended,
      onChange,
      error,
      useEquidistantScale = true,
      scale: customScale,
      premiums,
      loading,
      ...rest
    },
    ref
  ) => {
    const [recommendedValuePos, setRecommendedValuePos] = useState(0);
    const [localValue, setLocalValue] = useState(value);
    const isMobile = useMediaQuery('(max-width: 460px)');

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const debouncedOnChange = useDebouncedCallback((newValue: SumInsuredValue) => {
      onChange?.(newValue);
    }, 200);

    // Split covers into numeric (for scale math) and detect unlimited as last option
    const numericCovers = useMemo(
      () => (allowedCovers ?? []).filter((c): c is number => !isUnlimitedSI(c)),
      [allowedCovers]
    );
    const hasUnlimited = useMemo(
      () => (allowedCovers ?? []).some(isUnlimitedSI),
      [allowedCovers]
    );

    // Handle case when value is 0 or not in allowedCovers — set to first allowed cover
    useEffect(() => {
      if (allowedCovers && allowedCovers.length > 0) {
        if (value === 0) {
          debouncedOnChange(allowedCovers[0]);
        } else if (
          value !== undefined &&
          !isUnlimitedSI(value) &&
          value > 0 &&
          !allowedCovers.includes(value)
        ) {
          debouncedOnChange(allowedCovers[0]);
        }
      }
    }, [value, allowedCovers, debouncedOnChange]);

    // Generate marks from allowed covers if no marks are provided
    const marksFromAllowedCovers = useMemo(() => {
      if (!allowedCovers) return [];
      return allowedCovers.map((cover) => ({
        value: isUnlimitedSI(cover) ? Infinity : cover,
        label: formatCoverLabel(cover)
      }));
    }, [allowedCovers]);

    const derivedMarks = marks ?? marksFromAllowedCovers;

    // For scale math, use only finite mark values
    const finiteMarkValues = useMemo(
      () => derivedMarks.map((m) => m.value).filter(isFinite),
      [derivedMarks]
    );

    const originalMin = finiteMarkValues.length ? finiteMarkValues[0] : 0;
    const originalMax = finiteMarkValues.length
      ? finiteMarkValues[finiteMarkValues.length - 1]
      : 1_00_000_00;

    // actualValues for slider scale: numeric covers only; unlimited maps to the last slot
    const actualValues = useMemo(() => {
      const base = numericCovers.length ? numericCovers : finiteMarkValues;
      // When unlimited is present, add a placeholder at the end for the extra slot
      return hasUnlimited ? [...base, base[base.length - 1]] : base;
    }, [numericCovers, finiteMarkValues, hasUnlimited]);

    const min = useEquidistantScale ? 0 : originalMin;
    const max = useEquidistantScale ? 100 : originalMax;

    // Create equidistant visual marks
    const equidistantMarks = useMemo(() => {
      if (!useEquidistantScale || derivedMarks.length === 0) return derivedMarks;

      const totalSlots = actualValues.length;
      return (allowedCovers ?? []).map((cover, index) => ({
        value: index * (100 / (totalSlots - 1)),
        label: formatCoverLabel(cover)
      }));
    }, [derivedMarks, useEquidistantScale, actualValues, allowedCovers]);

    const activeMarks = useEquidistantScale ? equidistantMarks : derivedMarks;

    // Convert slider visual value → actual cover value
    const getScale = useCallback(() => {
      if (!useEquidistantScale) return (val: number) => val;

      return (sliderValue: number) => {
        const totalSlots = actualValues.length;
        const segmentSize = 100 / (totalSlots - 1);

        // Rightmost position → unlimited if available
        if (hasUnlimited && sliderValue >= 100 - segmentSize / 2) {
          return Infinity; // sentinel used internally; translated to 'unlimited' at onChange
        }

        // Single (or no) numeric cover: no range to interpolate across — avoids
        // numericCovers[-1] reads / NaN when allowedCovers is e.g. [10L, 'unlimited']
        if (numericCovers.length <= 1) {
          return numericCovers[0] ?? originalMin;
        }

        const index = Math.min(
          Math.floor(sliderValue / segmentSize),
          numericCovers.length - 2
        );

        const lowerMark = index * segmentSize;
        const upperMark = (index + 1) * segmentSize;
        const percentage = (sliderValue - lowerMark) / (upperMark - lowerMark);

        return (
          numericCovers[index] +
          percentage * (numericCovers[index + 1] - numericCovers[index])
        );
      };
    }, [useEquidistantScale, actualValues, hasUnlimited, numericCovers]);

    // Convert actual cover value → slider visual position
    const getInvertScale = useCallback(() => {
      if (!useEquidistantScale) return (val: number) => val;

      return (actualValue: number | SumInsuredValue) => {
        if (isUnlimitedSI(actualValue) || actualValue === Infinity) return 100;

        // Single (or no) numeric cover sits at the leftmost slot — the interpolation
        // loop below never runs and numericCovers[1] is undefined → NaN otherwise
        if (numericCovers.length <= 1) return 0;

        const totalSlots = actualValues.length;
        const segmentSize = 100 / (totalSlots - 1);

        let index = 0;
        for (let i = 0; i < numericCovers.length - 1; i++) {
          if (
            (actualValue as number) >= numericCovers[i] &&
            (actualValue as number) <= numericCovers[i + 1]
          ) {
            index = i;
            break;
          }
        }

        const lowerValue = numericCovers[index];
        const upperValue = numericCovers[index + 1];
        const percentage =
          ((actualValue as number) - lowerValue) / (upperValue - lowerValue);

        return index * segmentSize + percentage * segmentSize;
      };
    }, [useEquidistantScale, actualValues, numericCovers]);

    const scale = customScale || getScale();
    const invertScale = getInvertScale() as (val: number) => number;

    // Translate internal Infinity sentinel from scale() back to 'unlimited'
    const fireChange = useCallback(
      (raw: number | SumInsuredValue) => {
        if (raw === Infinity || isUnlimitedSI(raw)) {
          debouncedOnChange('unlimited');
        } else {
          debouncedOnChange(raw as number);
        }
      },
      [debouncedOnChange]
    );

    const incrementCover = useCallback(() => {
      if (localValue === undefined) return;

      if (isUnlimitedSI(localValue)) return; // already at max

      if (localValue < originalMax) {
        const nextNumeric = numericCovers.find((c) => c > (localValue as number));
        if (nextNumeric !== undefined) {
          setLocalValue(nextNumeric);
          fireChange(nextNumeric);
        }
      } else if (hasUnlimited) {
        // at last numeric cover — step to unlimited
        setLocalValue('unlimited');
        fireChange('unlimited');
      }
    }, [localValue, originalMax, numericCovers, hasUnlimited, fireChange]);

    const decrementCover = useCallback(() => {
      if (localValue === undefined) return;

      if (isUnlimitedSI(localValue)) {
        // step down to last numeric cover
        const lastNumeric = numericCovers[numericCovers.length - 1];
        if (lastNumeric !== undefined) {
          setLocalValue(lastNumeric);
          fireChange(lastNumeric);
        }
        return;
      }

      if ((localValue as number) > originalMin) {
        const prevValue = numericCovers
          ? [...numericCovers]
              .filter((cover) => cover < (localValue as number))
              .sort((a, b) => b - a)[0]
          : null;
        const newValue = prevValue ?? originalMin;
        setLocalValue(newValue);
        fireChange(newValue);
      }
    }, [localValue, originalMin, numericCovers, fireChange]);

    const findClosestMark = (value: number) => {
      if (!activeMarks || activeMarks.length === 0) return null;

      return activeMarks.reduce((closest, current) => {
        return Math.abs(current.value - value) < Math.abs(closest.value - value)
          ? current
          : closest;
      });
    };

    const calculateMarkPosition = (
      mark: { value: number; label?: ReactNode } | null
    ) => {
      if (!mark) return null;

      const range = max - min;
      if (range === 0) return 0;

      return ((mark.value - min) / range) * 100;
    };

    useEffect(() => {
      if (!recommended || activeMarks.length === 0) return;

      const sliderRecommended = useEquidistantScale
        ? invertScale(recommended)
        : recommended;

      const closestMark = findClosestMark(sliderRecommended);
      const position = calculateMarkPosition(closestMark);

      if (position !== undefined && position !== null)
        setRecommendedValuePos(position);
    }, [recommended, activeMarks, invertScale, useEquidistantScale]);

    // Slider value: for 'unlimited' use rightmost position (100 when equidistant, originalMax otherwise)
    const sliderValue = isUnlimitedSI(value)
      ? useEquidistantScale
        ? 100
        : originalMax
      : useEquidistantScale
      ? invertScale((value ?? originalMin) as number)
      : (value as number) ?? originalMin;

    const valueLabelFormat = (sliderValue: number) => {
      // Mantine passes scale(value) to the label; the equidistant scale returns
      // Infinity at the rightmost "unlimited" slot. Real covers are always finite.
      if (!isFinite(sliderValue)) return 'Unlimited';
      return convertToINR(sliderValue, 0);
    };

    // Calculate positions for all covers
    const coverPositions = useMemo(() => {
      if (!allowedCovers || allowedCovers.length === 0) return {};

      const positions: Record<string | number, number> = {};

      allowedCovers.forEach((coverValue) => {
        if (isUnlimitedSI(coverValue)) {
          positions['unlimited'] = 100;
          return;
        }

        const sliderVal = useEquidistantScale
          ? invertScale(coverValue as number)
          : (coverValue as number);

        const range = max - min;
        positions[coverValue as number] =
          range === 0 ? 0 : ((sliderVal - min) / range) * 100;
      });

      return positions;
    }, [allowedCovers, useEquidistantScale, invertScale, min, max]);

    const RecommendationDot = useMemo(() => {
      // The badge is positioned against the dot (its 16px-wide box), so centre it
      // on the dot and pull it inwards at the extremes to avoid overflowing.
      const badgePlacement =
        recommendedValuePos === 0
          ? { left: 0, transform: 'none' }
          : recommendedValuePos === 100
          ? { left: RECOMMENDATION_DOT_SIZE, transform: 'translateX(-100%)' }
          : { left: RECOMMENDATION_DOT_SIZE / 2, transform: 'translateX(-50%)' };

      return (
        recommended &&
        value && (
          <Box
            pos="absolute"
            left={getMarkAlignedLeft(recommendedValuePos, RECOMMENDATION_DOT_SIZE)}
            top={0}
            onClick={() => debouncedOnChange(recommended)}
          >
            <ExplainerPopover
              target={
                <Box>
                  <Flex
                    w="md"
                    h="md"
                    bg="primary.1"
                    style={{
                      borderRadius: 1000,
                      cursor: 'pointer'
                    }}
                    justify={'center'}
                    align={'center'}
                  >
                    <Box
                      w="xs"
                      h="xs"
                      bg="primary.5"
                      style={{ borderRadius: 1000, zIndex: 2 }}
                    />
                  </Flex>
                  <Badge
                    color="primary"
                    variant="outline"
                    bg="blue.0"
                    c="primary.4"
                    radius="xs"
                    pos="absolute"
                    top={60}
                    left={badgePlacement.left}
                    style={{
                      cursor: 'pointer',
                      transform: badgePlacement.transform
                    }}
                    size="lg"
                  >
                    Recommended
                  </Badge>
                </Box>
              }
              image={Recommendation}
              title={
                isUnlimitedSI(value)
                  ? `You've selected our highest coverage.`
                  : (value as number) < recommended
                  ? `Coverage of ${convertToINR(
                      value as number
                    )} is insufficient to meet all potential needs.`
                  : (value as number) > recommended
                  ? `You've selected coverage higher than our recommendation.`
                  : `You've selected our recommended coverage amount.`
              }
              subTitle={`Based on the age and location we recommended this ${convertToINR(
                recommended,
                0
              )}.`}
            />
          </Box>
        )
      );
    }, [recommended, recommendedValuePos, value]);

    const Premiums = useMemo(() => {
      const coverPositionsNow = coverPositions;
      const currentPremiumEntry = premiums?.find((premium) =>
        isUnlimitedSI(value ?? 0)
          ? isUnlimitedSI(premium.cover)
          : premium.cover === value
      );
      const currentPremium = currentPremiumEntry?.premium ?? 0;
      const extraPremiumsWillFit = derivedMarks?.length <= 10 && !isMobile;

      const getPremiumStyles = (coverPosition: number) => {
        if (coverPosition === 0) return { left: '-20px', ta: 'left' };
        if (coverPosition === 100) return { left: 'calc(100% - 80px)', ta: 'right' };
        return { left: `calc(${coverPosition}% - 50px)`, ta: 'center' };
      };

      const getExtraStyles = (coverPosition: number) => {
        if (coverPosition === 0) return { left: '0', ta: 'left' };
        if (coverPosition === 100)
          return {
            left: extraPremiumsWillFit
              ? `calc(${coverPosition}% - 40px)`
              : `calc(${coverPosition}% - 8px)`,
            ta: 'right'
          };
        return {
          left: extraPremiumsWillFit
            ? `calc(${coverPosition}% - 30px)`
            : `calc(${coverPosition}% - 8px)`,
          ta: 'center'
        };
      };

      const isCurrentCover = (premium: { cover: SumInsuredValue }) =>
        isUnlimitedSI(value ?? 0)
          ? isUnlimitedSI(premium.cover)
          : premium.cover === value;

      const isHigherCover = (premium: { cover: SumInsuredValue }) => {
        if (isUnlimitedSI(value ?? 0)) return false; // nothing is higher than unlimited
        if (isUnlimitedSI(premium.cover)) return true;
        return (premium.cover as number) > (value as number);
      };

      return (
        value &&
        premiums &&
        premiums.length > 0 && (
          <Group pos="relative" w="100%" justify="space-between">
            {premiums.map((premium) => {
              const coverKey = isUnlimitedSI(premium.cover)
                ? 'unlimited'
                : (premium.cover as number);
              const coverPosition = coverPositionsNow[coverKey] ?? 0;

              if (isCurrentCover(premium)) {
                const premiumStyle = getPremiumStyles(coverPosition);
                return (
                  <Stack
                    pos="absolute"
                    key={String(premium.cover)}
                    style={{
                      left: premiumStyle.left,
                      textAlign: premiumStyle.ta as 'left' | 'center' | 'right'
                    }}
                    top={40}
                    gap={0}
                    miw={100}
                  >
                    <Text fz={11} fw={600} c="dimmed">
                      {getHealthFrequencyValue(premium.premium)} /{' '}
                      {(premium.tenure ?? 1) > 1 ? `${premium.tenure} yrs` : 'yr'}
                    </Text>
                  </Stack>
                );
              } else if (isHigherCover(premium)) {
                const extraStyle = getExtraStyles(coverPosition);
                return extraPremiumsWillFit ? (
                  <Box
                    key={String(premium.cover)}
                    pos="absolute"
                    style={{ left: extraStyle.left }}
                    top={36}
                  >
                    <Badge
                      variant="transparent"
                      color="orange.3"
                      radius="xs"
                      fz={11}
                      fw={600}
                      size="lg"
                    >
                      +{getHealthFrequencyValue(premium.premium - currentPremium)}
                    </Badge>
                  </Box>
                ) : (
                  <ExplainerPopover
                    key={String(premium.cover)}
                    target={
                      <Box
                        pos="absolute"
                        style={{ left: extraStyle.left, cursor: 'pointer' }}
                        top={40}
                      >
                        <BsLightningChargeFill color="var(--mantine-color-creamorange-6)" />
                      </Box>
                    }
                    title={`+${getHealthFrequencyValue(
                      premium.premium - currentPremium
                    )} only`}
                    subTitle={`For coverage of ${
                      isUnlimitedSI(premium.cover)
                        ? 'Unlimited'
                        : convertToINR(premium.cover as number, 0)
                    }`}
                    icon={
                      <BsLightningChargeFill
                        color="var(--mantine-color-creamorange-6)"
                        size={28}
                      />
                    }
                  />
                );
              }
            })}
          </Group>
        )
      );
    }, [premiums, coverPositions, value, derivedMarks]);

    return (
      <Stack w="100%" pb={{ base: 84, sm: 72 }}>
        {title && (
          <Group justify="space-between">
            <Stack gap="xs">
              <Text fz={{ base: 14, sm: 18 }} fw={600} lh={1}>
                {title}
              </Text>
              <Text
                fz={13}
                c="secondary"
                maw={{ base: 200, sm: 300 }}
                pt={{ base: 0, sm: 4 }}
              >
                {description ?? 'Please select a cover'}
              </Text>
            </Stack>
            {coverChangeBox && (
              <Group
                px={12}
                py={14}
                w={140}
                wrap="nowrap"
                justify="space-between"
                className={classes.coverChangeBox}
                gap="xs"
              >
                <ActionIcon
                  size="sm"
                  variant="light"
                  color="secondary"
                  onClick={decrementCover}
                  disabled={
                    localValue === undefined ||
                    (!isUnlimitedSI(localValue) &&
                      (localValue as number) <= originalMin) ||
                    loading
                  }
                  aria-label="Decrease cover"
                >
                  <RiSubtractLine />
                </ActionIcon>
                <Text fz={16} lh={1} fw={500}>
                  {isUnlimitedSI(localValue ?? 0) ? (
                    <RiInfinityLine
                      size={20}
                      aria-label="Unlimited"
                      style={{ verticalAlign: 'middle' }}
                    />
                  ) : (
                    convertToINR((localValue as number) ?? 0, 0)
                  )}
                </Text>
                <ActionIcon
                  size="sm"
                  variant="light"
                  color="secondary"
                  onClick={incrementCover}
                  disabled={
                    localValue === undefined ||
                    isUnlimitedSI(localValue) ||
                    ((localValue as number) >= originalMax && !hasUnlimited) ||
                    loading
                  }
                  aria-label="Increase cover"
                >
                  <RiAddLine />
                </ActionIcon>
              </Group>
            )}
          </Group>
        )}
        <Box pos="relative">
          <Slider
            mod={{}}
            ref={ref}
            value={sliderValue}
            marks={activeMarks}
            min={min}
            max={max}
            onChange={(newValue) => {
              const actualValue = scale(newValue);
              fireChange(actualValue);
            }}
            scale={scale}
            restrictToMarks={restrictToMarks}
            color="primary"
            thumbChildren={<RiShieldFill size={12} />}
            thumbSize={24}
            classNames={{
              thumb: classes.thumb,
              bar: classes.bar,
              markLabel: classes.markLabel,
              markWrapper: classes.markWrapper,
              mark: classes.mark
            }}
            {...rest}
            label={valueLabelFormat}
          />
          {recommended && RecommendationDot}
          {premiums && premiums.length > 0 && Premiums}
        </Box>
        {error && <Input.Error>{error}</Input.Error>}
      </Stack>
    );
  }
);

export { SliderCoverPicker };
export type { SliderCoverPickerProps };
